"use client";

/**
 * Residence comments - the community section on /residence/{clusterId}.
 *
 * Renders the APPROVED comments (fetched at runtime - the page itself
 * is ISR-cached for a day, so comments can't be baked into the server
 * payload), plus the caller's own single editable comment:
 *   - anonymous: a sign-in CTA card (return_to comes back here);
 *   - signed-in: a textarea prefilled with their comment; submitting
 *     (or editing) sends it to review - nothing shows publicly until
 *     an admin approves it on the Residence inbox page.
 */
import { useCallback, useEffect, useState } from "react";
import { MessageSquareText, Loader2, Clock3 } from "lucide-react";
import Link from "@/lib/localized-link";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import type { Locale } from "@/lib/i18n-config";
import { t as translate, type TranslationKey } from "@/lib/i18n";
import { BUTTON_SECONDARY } from "@/lib/ui-classes";

const BODY_MAX = 600;

interface PublicComment {
  author: string | null;
  body: string;
  created_at: string;
}

interface MyComment {
  exists: boolean;
  body?: string;
  status?: string;
}

export default function ResidenceComments({
  clusterId,
  locale,
}: {
  clusterId: string;
  locale: Locale;
}) {
  const t = useCallback(
    (k: TranslationKey) => translate(k, locale),
    [locale],
  );
  const { user, loading: authLoading } = useAuth();

  const [comments, setComments] = useState<PublicComment[] | null>(null);
  const [mine, setMine] = useState<MyComment | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetch(`${API_BASE}/residences/${encodeURIComponent(clusterId)}/comments`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d) setComments(d.items ?? []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clusterId]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void fetch(
      `${API_BASE}/residences/${encodeURIComponent(clusterId)}/comments/me`,
      { credentials: "include" },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d: MyComment | null) => {
        if (!cancelled && d) {
          setMine(d);
          if (d.exists && d.body) setBody(d.body);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, clusterId]);

  async function submit() {
    const text = body.trim();
    if (text.length < 3 || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(
        `${API_BASE}/residences/${encodeURIComponent(clusterId)}/comments`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", "X-Locale": locale },
          credentials: "include",
          body: JSON.stringify({ body: text }),
        },
      );
      if (!res.ok) {
        setErr(t("residence.comments.error"));
        return;
      }
      setMine({ exists: true, body: text, status: "pending" });
      setJustSubmitted(true);
    } catch {
      setErr(t("residence.comments.error"));
    } finally {
      setBusy(false);
    }
  }

  const dateFmt = new Intl.DateTimeFormat(locale, {
    day: "numeric", month: "short", year: "numeric",
  });

  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <MessageSquareText className="w-4 h-4 text-gray-400" />
        {t("residence.comments.title")}
      </h2>

      {/* Approved comments */}
      {comments && comments.length > 0 ? (
        <ul className="space-y-3 mb-5">
          {comments.map((c, i) => (
            <li key={i} className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="text-xs font-semibold text-gray-900">
                  {c.author || t("residence.comments.author_fallback")}
                </span>
                <span className="text-[11px] text-gray-400">
                  {dateFmt.format(new Date(c.created_at))}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-line">
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      ) : comments ? (
        <p className="text-xs text-gray-400 mb-5">
          {t("residence.comments.empty")}
        </p>
      ) : null}

      {/* Own-comment editor / sign-in CTA */}
      {authLoading ? null : !user ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-600 mb-3">
            {t("residence.comments.signin_title")}
          </p>
          <Link
            href={`/auth/login?return_to=${encodeURIComponent(`/residence/${clusterId}`)}`}
            rel="nofollow"
            className={`inline-flex items-center rounded-xl px-4 py-2 text-xs font-semibold transition-colors ${BUTTON_SECONDARY}`}
          >
            {t("residence.comments.signin_cta")}
          </Link>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          {mine?.exists && mine.status === "pending" && (
            <p className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
              <Clock3 className="w-3 h-3" />
              {t("residence.comments.pending_chip")}
            </p>
          )}
          {mine?.exists && mine.status === "rejected" && !justSubmitted && (
            <p className="mb-2 text-[11px] text-gray-500">
              {t("residence.comments.rejected_note")}
            </p>
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            rows={3}
            placeholder={t("residence.comments.placeholder")}
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-[11px] text-gray-400">
              {body.length}/{BODY_MAX} - {t("residence.comments.review_note")}
            </span>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || body.trim().length < 3}
              className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {mine?.exists
                ? t("residence.comments.update")
                : t("residence.comments.submit")}
            </button>
          </div>
          {err && (
            <p className="mt-2 text-xs font-medium text-rose-600">{err}</p>
          )}
        </div>
      )}
    </section>
  );
}
